import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

export interface ToastOptions {
  /** A button on the toast (Undo). Pressing it closes the toast. */
  action?: { label: string; onClick: () => void };
  /** Clicking the toast itself (a new message opens its conversation). */
  onClick?: () => void;
  /** How long it shows; default 3 s (6 s for errors). */
  ms?: number;
  /** Replaces an open toast with the same key (one Undo at a time for one task). */
  key?: string;
  testId?: string;
}

interface Toast extends ToastOptions { id: number; text: string; kind: 'info' | 'error' }
type Push = (text: string, kind?: Toast['kind'], opts?: ToastOptions) => void;
const Ctx = createContext<Push>(() => undefined);

/** Every action gives feedback within 100 ms (brief: quality bar). An Undo toast stays 4 s (docs/adr/0095). */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current.get(id));
    timers.current.delete(id);
    setToasts((t) => t.filter((x) => x.id !== id));
  }, []);
  const push = useCallback<Push>((text, kind = 'info', opts = {}) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...(opts.key ? t.filter((x) => x.key !== opts.key) : t), { id, text, kind, ...opts }]);
    timers.current.set(id, setTimeout(() => dismiss(id), opts.ms ?? (kind === 'error' ? 6000 : 3000)));
  }, [dismiss]);
  return (
    <Ctx.Provider value={push}>
      {children}
      <div className="toast-wrap" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast ${t.kind === 'error' ? 'error' : ''} ${t.onClick ? 'is-link' : ''}`}
            data-testid={t.testId}
            role={t.onClick ? 'button' : undefined}
            tabIndex={t.onClick ? 0 : undefined}
            onClick={t.onClick ? () => { t.onClick?.(); dismiss(t.id); } : undefined}
            onKeyDown={t.onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); t.onClick?.(); dismiss(t.id); } } : undefined}
          >
            <span className="grow">{t.text}</span>
            {t.action && (
              <button type="button" className="toast-action" onClick={(e) => { e.stopPropagation(); t.action?.onClick(); dismiss(t.id); }}>
                {t.action.label}
              </button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
